import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDownIcon, KanbanIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";

import { candidacyStatusOptions } from "@/lib/constants";
import { CandidacyStatus } from "@/types";

const jobPipelineStatusFilterSchema = z.object({
  statuses: z.array(z.string()),
});

export const JobPipelineStatusFilter = ({
  disabled,
  isInDefaultState,
  setStatuses,
  statuses,
}: {
  disabled?: boolean;
  isInDefaultState: boolean;
  setStatuses: (statuses: CandidacyStatus[]) => void;
  statuses: CandidacyStatus[];
}) => {
  const [open, setOpen] = useState(false);

  const form = useForm<z.infer<typeof jobPipelineStatusFilterSchema>>({
    defaultValues: { statuses },
    resolver: zodResolver(jobPipelineStatusFilterSchema),
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    // If we are closing the dropdown here it means we haven't applied
    // the filter, so reset the form to the current statuses.
    //
    // We also delay the reset slightly to avoid the reset happening
    // while the dropdown is closing.
    if (!open) {
      setTimeout(() => {
        form.reset({ statuses });
      }, 150);
    }
  };

  const handleSubmit = (
    data: z.infer<typeof jobPipelineStatusFilterSchema>,
  ) => {
    setStatuses(data.statuses as CandidacyStatus[]);
    setOpen(false);
  };

  // Need to reset the form when statuses change as a result of global reset.
  useEffect(() => {
    form.reset({ statuses });
  }, [form, statuses]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger disabled={disabled} asChild>
        <Button
          className="rounded-full"
          variant={isInDefaultState ? "outlineSecondary" : "filterOrSortActive"}
        >
          <KanbanIcon className="h-4 w-4" />
          Stage
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="statuses"
              render={() => (
                <FormItem className="p-4">
                  <FormLabel>Stage</FormLabel>
                  {candidacyStatusOptions.map((candidacyStatusOption) => (
                    <FormField
                      key={candidacyStatusOption.value}
                      control={form.control}
                      name="statuses"
                      render={({ field }) => {
                        return (
                          <FormItem className="flex gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value.includes(
                                  candidacyStatusOption.value,
                                )}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([
                                      ...field.value,
                                      candidacyStatusOption.value,
                                    ]);
                                  } else {
                                    field.onChange(
                                      field.value.filter(
                                        (value) =>
                                          (value as CandidacyStatus) !==
                                          candidacyStatusOption.value,
                                      ),
                                    );
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {candidacyStatusOption.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </FormItem>
              )}
            />
            <div className="flex justify-end border-t border-slate-200 p-2">
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </div>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
